import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Hook to fetch all tags for the organization
 * Filters out soft-deleted tags by default
 */
export function useTags(includeDeleted: boolean = false) {
  return useQuery({
    queryKey: ['tags', includeDeleted],
    queryFn: async () => {
      const response = await api.getTags();
      // Filter out soft-deleted tags unless specifically requested
      const tags = includeDeleted
        ? response.data
        : response.data.filter((tag) => !tag.is_deleted);
      return tags;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const response = await api.createTag(name, color);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate tags query to refetch
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

/**
 * Hook to update an existing tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      color,
    }: {
      id: number;
      name?: string;
      color?: string;
    }) => {
      const response = await api.updateTag(id, { name, color });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

/**
 * Hook to soft delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.deleteTag(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

/**
 * Hook to get active (non-deleted) tags only
 * This is a convenience hook that's commonly used
 */
export function useActiveTags() {
  return useTags(false);
}
